/**
 * Pure placement maths for the map key (MapLegend.tsx). No React / React Native
 * imports, so unit tests and tooling can use it directly.
 */

/** Distances from each side of the map wrapper to the drawn map's outer edges. */
export interface MapContentInsets { top: number; right: number; bottom: number; left: number }

/**
 * Where the drawn map's outer edges sit inside the map wrapper, in screen
 * pixels: the territories' bounding box, mapped through the SVG's viewBox
 * (scaled to fit, centred) and the wrapper's horizontal letterbox. The key's
 * corner lines up with these edges, e.g. bottom-right sits level with the
 * map's lowest point and flush with its rightmost point.
 */
/** A map (viewBox) y coordinate as pixels from the top of the map wrapper. */
export function mapYToWrapper(y: number, viewBox: { y: number; width: number; height: number }, svg: { width: number; height: number }): number {
  const scale = Math.min(svg.width / viewBox.width, svg.height / viewBox.height)
  return (svg.height - viewBox.height * scale) / 2 + (y - viewBox.y) * scale
}

export function mapContentInsets(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  viewBox: { x: number; y: number; width: number; height: number },
  svg: { width: number; height: number; left: number },
  containerWidth: number,
): MapContentInsets {
  const scale = Math.min(svg.width / viewBox.width, svg.height / viewBox.height)
  const ox = svg.left + (svg.width - viewBox.width * scale) / 2
  const oy = (svg.height - viewBox.height * scale) / 2
  const at = (x: number, y: number) => [ox + (x - viewBox.x) * scale, oy + (y - viewBox.y) * scale] as const
  const [left, top] = at(bounds.minX, bounds.minY)
  const [right, bottom] = at(bounds.maxX, bounds.maxY)
  return {
    top: Math.max(0, top),
    left: Math.max(0, left),
    right: Math.max(0, containerWidth - right),
    bottom: Math.max(0, svg.height - bottom),
  }
}
